import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { isPublicRoute } from './routesPubliques'; // 🚨 routes publiques : pas de redirection
import { useAuth } from './AuthContext';
import { reportClientEvent } from './clientDiagnostics';
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples

// 🔒 Cat H — Infrastructure RBAC : ce fichier EST le moteur central de permissions.
//   Tous les `estProprietaire(user)` ci-dessous sont des short-circuits d'identité
//   Owner (règle métier fondamentale : le propriétaire voit tout, toujours). Ce ne sont
//   PAS des gates RBAC hardcodés à purger — ils font partie intégrante de l'architecture
//   du contexte de permissions (voir CATALOGUE_ACTIONS_PAR_PAGE_V13_61.md §13.3).

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const PermissionsContext = createContext(null);

// Propriétaire a toujours tout
const OWNER_PERMS = { pages: null, actions: null }; // null = all true

// 🆕 [Phase 26 P0] Self-healing : après MAX_ATTEMPTS échouées, on reprogramme un retry silencieux
// toutes les BACKGROUND_RETRY_INTERVAL_MS pour récupérer dès que le réseau revient.
const BACKGROUND_RETRY_INTERVAL_MS = 30 * 1000; // 30s
const MAX_BACKGROUND_RETRIES = 10; // ~5min de tentatives silencieuses

// Types d'events VALID pour telemetry — keep in sync with backend `client_diagnostics.py`
// 🆕 [F5] Pas besoin d'export, c'est juste pour clarifier ci-dessous.

export function PermissionsProvider({ children }) {
  const { user, token, logout } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  // 🆕 2026-05-26 — État de fetch pour détecter les pannes réseau (sidebar vide silencieuse)
  const [fetchError, setFetchError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  // 🆕 [Phase 26 P0] Compteur de retries silencieux en arrière-plan (post-échec)
  const backgroundRetryTimerRef = useRef(null);
  const backgroundRetryCountRef = useRef(0);
  const isFetchingRef = useRef(false);

  const fetchPermissions = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setFetchError(null);

    // 🆕 [F5 — 2026-06-01] Distingue 3 sous-cas qui produisent `permissions=null` :
    //   1. noUser : AuthProvider pas encore résolu (race au mount)
    //      → on garde `loading=true` (skeleton) ; un useEffect retry quand user arrive
    //   2. tokenExpired : user existe mais token absent
    //      → force logout (le AuthContext gère le redirect)
    //   3. proprietaire : accès total immédiat
    if (!user) {
      // Race au mount : on n'a pas encore le user. Garde `loading=true` pour
      // afficher le skeleton plutôt qu'un menu vide silencieux. Le 2e useEffect
      // (watch [user, token]) déclenchera le fetch dès que user arrive.
      try {
        reportClientEvent('permissions_fetch_failed', 'warning', {
          recovery_trigger: 'skipped_no_user',
          retry_count: retryCount,
        });
      } catch { /* */ }
      setLoading(true);
      setPermissions(null);
      isFetchingRef.current = false;
      return;
    }
    if (!token) {
      // User présent mais token manquant → session corrompue. Force logout.
      try {
        reportClientEvent('permissions_fetch_failed', 'error', {
          recovery_trigger: 'skipped_no_token',
          role: user?.role || '',
        });
      } catch { /* */ }
      setLoading(false);
      setPermissions(null);
      isFetchingRef.current = false;
      try { logout && logout(); } catch { /* */ }
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login') && !isPublicRoute(window.location.pathname)) {
        window.location.href = '/login';
      }
      return;
    }
    if (estProprietaire(user)) {
      setPermissions(OWNER_PERMS); setLoading(false); isFetchingRef.current = false; return;
    }
    // 👥 E3b — source de vérité : `permissions_effectives` calculées côté serveur (/auth/me, login) ; plus de déduction locale par rôle
    if (user.permissions_effectives && typeof user.permissions_effectives.pages === 'object' && Object.keys(user.permissions_effectives.pages).length > 0) {
      setPermissions(user.permissions_effectives); setFetchError(null); setLoading(false); isFetchingRef.current = false; return;
    }

    setLoading(true);
    setPermissions(null);

    // 🆕 [Phase 26 P0] Backoff resserré : 500ms / 1s / 2s (au lieu de 1s/2s/4s).
    // Permet de récupérer plus vite d'un blip réseau temporaire (mobile LTE).
    const MAX_ATTEMPTS = 3;
    const BACKOFFS_MS = [500, 1000, 2000];
    let lastError = null;
    const fetchStartedAt = Date.now();
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      const attemptStart = Date.now();
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
        const res = await fetch(`${API_URL}/api/permissions/${user.role}?_=${Date.now()}`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (res.ok) {
          const data = await res.json();
          // ✅ Validation minimale : on doit avoir au moins `pages` avec des clés
          if (data && typeof data.pages === 'object' && Object.keys(data.pages || {}).length > 0) {
            setPermissions(data);
            setFetchError(null);
            setLoading(false);
            // Reset des retries arrière-plan : succès
            backgroundRetryCountRef.current = 0;
            if (backgroundRetryTimerRef.current) {
              clearTimeout(backgroundRetryTimerRef.current);
              backgroundRetryTimerRef.current = null;
            }
            isFetchingRef.current = false;
            return;
          }
          lastError = `Réponse serveur invalide (pages manquantes)`;
        } else {
          lastError = `HTTP ${res.status}`;
        }
      } catch (e) {
        lastError = e?.name === 'AbortError' ? 'Timeout (10s)' : (e?.message || 'Erreur réseau');
      }
      // 🆕 [2A.3] Télescope : on log chaque retry échoué (warning)
      // 🆕 [Phase 26 P0] Tag spécifique si signature "no global object" (Safari/iOS background)
      const isNoGlobalObject = /no global object|global object/i.test(String(lastError || ''));
      try {
        reportClientEvent('permissions_fetch_failed', 'warning', {
          attempt: attempt + 1,
          max_attempts: MAX_ATTEMPTS,
          role: user?.role || '',
          error: String(lastError || '').slice(0, 200),
          duration_ms: Date.now() - attemptStart,
          no_global_object: isNoGlobalObject,
          visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
        });
      } catch { /* silencieux */ }
      // Backoff: 500ms / 1s / 2s
      if (attempt < MAX_ATTEMPTS - 1) {
        await new Promise(r => setTimeout(r, BACKOFFS_MS[attempt]));
      }
    }
    // Après MAX_ATTEMPTS, on signale l'erreur pour que l'UI puisse afficher un message
    // au lieu de garder une sidebar vide silencieuse.
    console.error('[PermissionsContext] Échec après', MAX_ATTEMPTS, 'tentatives:', lastError);
    setPermissions(null);
    setFetchError(lastError || 'Erreur inconnue');
    setLoading(false);
    isFetchingRef.current = false;
    // 🆕 [2A.3] Télescope : échec final (error)
    try {
      reportClientEvent('permissions_fetch_failed', 'error', {
        final: true,
        role: user?.role || '',
        error: String(lastError || '').slice(0, 200),
        total_duration_ms: Date.now() - fetchStartedAt,
        no_global_object: /no global object|global object/i.test(String(lastError || '')),
        background_retry_count: backgroundRetryCountRef.current,
      });
    } catch { /* silencieux */ }
    // 🆕 [Phase 26 P0] Self-healing : programmer un retry silencieux dans 30s (max 10×).
    // Si le réseau revient pendant ce temps, la sidebar se remplit toute seule sans
    // intervention user.
    if (backgroundRetryCountRef.current < MAX_BACKGROUND_RETRIES) {
      if (backgroundRetryTimerRef.current) clearTimeout(backgroundRetryTimerRef.current);
      backgroundRetryTimerRef.current = setTimeout(() => {
        backgroundRetryCountRef.current += 1;
        backgroundRetryTimerRef.current = null;
        setRetryCount(c => c + 1);
      }, BACKGROUND_RETRY_INTERVAL_MS);
    }
  }, [user, token, retryCount]);

  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);

  // 🆕 [Phase 26 P0] Self-healing event-driven : auto-retry lors d'événements de
  // récupération système (réseau revenu, onglet réactivé, fenêtre focus).
  // Ces 3 events couvrent les 3 scénarios principaux de bug sidebar vide :
  //   - LTE coupé → revenu (online)
  //   - Mobile mis en veille → réveil (visibilitychange)
  //   - Onglet en arrière-plan sur desktop → revenu au premier plan (focus)
  useEffect(() => {
    if (!user || estProprietaire(user)) return;
    const tryRecover = (reason) => {
      // Ne retry que si on est en état "échoué" ou "permissions absentes"
      const hasIssue = fetchError !== null || (!loading && (!permissions || !permissions.pages));
      if (!hasIssue) return;
      try {
        reportClientEvent('permissions_fetch_failed', 'warning', {
          recovery_trigger: reason,
          role: user?.role || '',
          had_error: !!fetchError,
        });
      } catch { /* */ }
      // Reset background retries pour repartir frais
      backgroundRetryCountRef.current = 0;
      if (backgroundRetryTimerRef.current) {
        clearTimeout(backgroundRetryTimerRef.current);
        backgroundRetryTimerRef.current = null;
      }
      setRetryCount(c => c + 1);
    };
    const onOnline = () => tryRecover('online');
    const onVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        tryRecover('visibilitychange');
      }
    };
    const onFocus = () => tryRecover('focus');
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, fetchError, loading, permissions]);

  // Cleanup background retry timer au démontage
  useEffect(() => () => {
    if (backgroundRetryTimerRef.current) {
      clearTimeout(backgroundRetryTimerRef.current);
      backgroundRetryTimerRef.current = null;
    }
  }, []);

  // 🆕 [2A.3] Watchdog : si `loading` reste à `true` > 15s alors qu'on a un user
  // valide (non-proprietaire), on log un event critical. Permet d'attraper les
  // cas "sidebar vide silencieuse en attente" sur LTE faible / Safari iOS.
  const stuckReportedRef = useRef(false);
  useEffect(() => {
    if (!user || estProprietaire(user)) return;
    if (!loading) { stuckReportedRef.current = false; return; }
    const timeoutId = setTimeout(() => {
      if (stuckReportedRef.current) return;
      stuckReportedRef.current = true;
      try {
        reportClientEvent('auth_loading_stuck', 'critical', {
          role: user?.role || '',
          retry_count: retryCount,
          has_token: !!token,
        });
      } catch { /* silencieux */ }
    }, 15000);
    return () => clearTimeout(timeoutId);
  }, [loading, user, token, retryCount]);

  // 🆕 2026-05-26 — Permettre un retry manuel depuis l'UI (bouton "Réessayer")
  const retryFetch = useCallback(() => {
    setRetryCount(c => c + 1);
  }, []);

  /**
   * 🆕 [F5] Mode dégradé — quand `fetchError` est posé mais que le user/token sont OK.
   *
   * 🚨 [Phase 1.b Hotfix Sécurité — 2026-07-18] BASCULEMENT FAIL-OPEN → FAIL-SECURE.
   *   Avant : `canSeePage` retournait `true` en mode dégradé (best-effort UX). Résultat :
   *   quand le fetch permissions échouait (réseau intermittent, backend lent), TOUS les
   *   employés non-owner voyaient TOUTES les pages → brèche de confidentialité intermittente.
   *   Après : mode dégradé accorde l'accès UNIQUEMENT au propriétaire (short-circuit).
   *   Tous les autres rôles sont bloqués (fail-secure) — cohérent avec le principe
   *   « Backend = source unique de vérité ».
   */
  const degradedMode = !!(user && token && fetchError && !permissions);

  /**
   * 🚀 [Phase 1.b Hotfix Sécurité — 2026-07-18] Flag `permissionsLoaded`.
   *   True dès qu'un fetch réussi a peuplé `permissions.pages`. Utilisé par le composant
   *   <PageGuard> pour BLOQUER le rendu des routes protégées tant que les permissions
   *   ne sont pas connues (fail-secure race condition). Owner reste short-circuit.
   */
  const permissionsLoaded = estProprietaire(user)
    || (!loading && !!permissions?.pages);

  /**
   * canSeePage(pageKey) → boolean
   * canDo(actionKey) → boolean
   */
  const canSeePage = useCallback((pageKey) => {
    if (!user) return false;
    if (estProprietaire(user)) return true;
    if (loading) return false; // Chargement en cours → masquer par défaut (sécurité)
    // 🚨 [Phase 1.b Hotfix Sécurité] Mode dégradé = FAIL-SECURE : seul l'owner passe.
    //   L'ancien comportement (return true) créait une brèche intermittente pour
    //   tous les rôles non-owner quand le fetch permissions échouait.
    if (degradedMode) return false;
    if (!permissions?.pages) return false;
    return permissions.pages[pageKey] === true;
  }, [user, permissions, loading, degradedMode]);

  /**
   * ✅ Détecte si une permission est RESTRICTIVE (limite au lieu d'accorder).
   * Convention : toute clé se terminant par "_seulement" ou commençant par "restrict_".
   * Pour ces permissions, le propriétaire retourne TOUJOURS false (aucune restriction ne s'applique à lui).
   */
  const isRestrictive = (key) => {
    if (!key || typeof key !== 'string') return false;
    return key.endsWith('_seulement') || key.startsWith('restrict_');
  };

  const canDo = useCallback((actionKey) => {
    if (!user) return false;
    if (estProprietaire(user)) return !isRestrictive(actionKey);
    if (loading) return false;
    if (!permissions?.actions) return false;
    return permissions.actions[actionKey] === true;
  }, [user, permissions, loading]);

  const canSeeEmployeTab = useCallback((tabKey) => {
    if (!user) return false;
    if (estProprietaire(user)) return true;
    if (loading) return false;
    if (!permissions?.employe_tabs) return false;
    return permissions.employe_tabs[tabKey] === true;
  }, [user, permissions, loading]);

  const canSeeDossierTab = useCallback((tabKey) => {
    if (!user) return false;
    if (estProprietaire(user)) return true;
    if (loading || !permissions?.dossier_onglets) return false;
    return permissions.dossier_onglets[tabKey] === true;
  }, [user, permissions, loading]);

  const canDoBonTravailAction = useCallback((actionKey) => {
    if (!user) return false;
    if (estProprietaire(user)) return true;
    const ROLES_AUTORISES_PAR_DEFAUT = ['charge_projet', 'adjoint_admin'];
    const isPrivilegedRole = aLeRole(user, ...ROLES_AUTORISES_PAR_DEFAUT);
    if (loading) return isPrivilegedRole;
    if (!permissions?.bon_travail_actions) return isPrivilegedRole;
    return permissions.bon_travail_actions[actionKey] === true;
  }, [user, permissions, loading]);

  return (
    <PermissionsContext.Provider value={{ permissions, loading, permissionsLoaded, fetchError, degradedMode, retryFetch, canSeePage, canDo, canSeeEmployeTab, canSeeDossierTab, canDoBonTravailAction, refetch: fetchPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export const usePermissions = () => useContext(PermissionsContext);

/**
 * 🆕 [Phase v13.59 — 2026-08-05] Hook dédié pour les permissions d'action dynamiques.
 *
 * Retourne un booléen indiquant si l'utilisateur courant peut exécuter l'action
 * `actionKey` (ex: `bt_travaux_termines_uncheck`). Basé sur la matrice DB
 * `role_permissions[role].actions[action_key]`, alignée avec le guard backend
 * `require_action_permission`.
 *
 * Contrat :
 *   - Owner (proprietaire) → toujours true (short-circuit fondation).
 *   - Autres rôles → true UNIQUEMENT si `permissions.actions[actionKey] === true`.
 *   - Loading / permissions absents → false (fail-secure).
 *
 * Usage :
 *   const canUncheck = useActionPermission('bt_travaux_termines_uncheck');
 *   if (canUncheck) return <BtnDecocher />;
 */
export const useActionPermission = (actionKey) => {
  const ctx = useContext(PermissionsContext) || {};
  const canDoFn = ctx.canDo;
  if (typeof canDoFn !== 'function') return false;
  if (!actionKey || typeof actionKey !== 'string') return false;
  return canDoFn(actionKey);
};
